// frontend/src/modules/Properties/components/GalleryPreview.tsx
// Manages which 5 photos appear in the property detail gallery on warm-client
import { useState, useEffect } from 'react';
import {
  Card, Stack, Group, Text, Image, SimpleGrid, ActionIcon, Badge,
  ThemeIcon, Button, Alert, Tooltip, Paper, Modal
} from '@mantine/core';
import {
  IconPhoto, IconStarFilled,
  IconPlus, IconX, IconInfoCircle, IconArrowUp, IconArrowDown,
  IconArrowsExchange
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { propertiesApi } from '@/api/properties.api';

interface Photo {
  id: number;
  photo_url: string;
  category: string;
  sort_order: number;
  is_primary: boolean;
  featured_order: number | null;
}

interface GalleryPreviewProps {
  propertyId: number;
  viewMode?: boolean;
}

const getPhotoUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${import.meta.env.VITE_API_URL?.replace('/api', '') || ''}${url}`;
};

const GalleryPreview = ({ propertyId, viewMode = false }: GalleryPreviewProps) => {
  const [featured, setFeatured] = useState<Photo[]>([]);
  const [allPhotos, setAllPhotos] = useState<Photo[]>([]);
  const [_loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [replaceSlot, setReplaceSlot] = useState<number | null>(null); // which slot to replace (1-5)

  useEffect(() => {
    if (propertyId) fetchFeatured();
  }, [propertyId]);

  const fetchFeatured = async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await propertiesApi.getFeaturedPhotos(propertyId);
      setFeatured(res.data.data.featured || []);
      setAllPhotos(res.data.data.allPhotos || []);
    } catch (e) {
      console.error('Error fetching featured:', e);
    } finally {
      setLoading(false);
    }
  };

  const saveFeatured = async (newFeatured: Photo[]) => {
    try {
      const updates = newFeatured.map((p, i) => ({ id: p.id, featured_order: i + 1 }));
      await propertiesApi.updateFeaturedPhotos(propertyId, updates);
      setFeatured(newFeatured);
      notifications.show({ title: 'Success', message: 'Gallery photos updated', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save', color: 'red' });
    }
  };

  const moveUp = (index: number) => {
    if (index <= 0) return;
    const arr = [...featured];
    [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
    saveFeatured(arr);
  };

  const moveDown = (index: number) => {
    if (index >= featured.length - 1) return;
    const arr = [...featured];
    [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
    saveFeatured(arr);
  };

  const removeFromFeatured = (index: number) => {
    const arr = featured.filter((_, i) => i !== index);
    saveFeatured(arr);
  };

  const addToFeatured = (photo: Photo) => {
    if (featured.length >= 5) {
      notifications.show({ title: 'Limit reached', message: 'Maximum 5 gallery photos', color: 'orange' });
      return;
    }
    if (featured.find(f => f.id === photo.id)) {
      notifications.show({ title: 'Already added', message: 'This photo is already in the gallery', color: 'orange' });
      return;
    }
    saveFeatured([...featured, photo]);
    setShowPicker(false);
  };

  const replaceInFeatured = (photo: Photo) => {
    if (replaceSlot === null) return;
    if (featured.find(f => f.id === photo.id)) {
      notifications.show({ title: 'Already added', message: 'This photo is already in the gallery', color: 'orange' });
      return;
    }
    const arr = [...featured];
    arr[replaceSlot] = photo;
    saveFeatured(arr);
    setShowPicker(false);
    setReplaceSlot(null);
  };

  const availablePhotos = allPhotos.filter(p => !featured.find(f => f.id === p.id));

  // Build the 5-slot gallery preview (matching warm-client layout)
  const slots = Array.from({ length: 5 }, (_, i) => featured[i] || null);

  if (!propertyId) return null;

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <ThemeIcon size="lg" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
              <IconPhoto size={20} />
            </ThemeIcon>
            <div>
              <Text fw={600} size="md">Gallery Preview</Text>
              <Text size="xs" c="dimmed">These 5 photos appear on the property detail page</Text>
            </div>
          </Group>
          {!viewMode && featured.length < 5 && (
            <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
              onClick={() => { setReplaceSlot(null); setShowPicker(true); }}>
              Add Photo
            </Button>
          )}
        </Group>

        <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />} mb="md">
          <Text size="xs">
            <strong>Position 1</strong> = Main hero photo (large, left side).
            <strong> Positions 2-5</strong> = Secondary grid photos (right side).
            The main photo is also used as the cover everywhere.
            Drag to reorder or click arrows to change positions.
          </Text>
        </Alert>

        {/* Gallery Grid Preview - mimics warm-client layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gridTemplateRows: '120px 120px', gap: '4px', borderRadius: '8px', overflow: 'hidden', marginBottom: '16px' }}>
          {/* Slot 1: Hero (spans 2 rows) */}
          <div style={{ gridRow: '1 / 3', position: 'relative' }}>
            {slots[0] ? (
              <>
                <Image src={getPhotoUrl(slots[0].photo_url)} alt="Hero" h="100%" w="100%" fit="cover" />
                <Badge pos="absolute" top={4} left={4} size="xs" color="yellow" variant="filled" leftSection={<IconStarFilled size={10} />}>Main</Badge>
                {!viewMode && (
                  <Group pos="absolute" bottom={4} right={4} gap={2}>
                    <Tooltip label="Replace"><ActionIcon size="xs" variant="filled" color="dark" onClick={() => { setReplaceSlot(0); setShowPicker(true); }}><IconArrowsExchange size={10} /></ActionIcon></Tooltip>
                  </Group>
                )}
              </>
            ) : (
              <Paper h="100%" bg="gray.1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: viewMode ? 'default' : 'pointer' }}
                onClick={() => !viewMode && (setReplaceSlot(null), setShowPicker(true))}>
                <Stack align="center" gap={4}><IconPlus size={24} color="gray" /><Text size="xs" c="dimmed">Add hero</Text></Stack>
              </Paper>
            )}
          </div>

          {/* Slots 2-5 */}
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ position: 'relative' }}>
              {slots[i] ? (
                <>
                  <Image src={getPhotoUrl(slots[i]!.photo_url)} alt={`Photo ${i + 1}`} h="100%" w="100%" fit="cover" />
                  <Badge pos="absolute" top={2} left={2} size="xs" variant="light" color="dark">{i + 1}</Badge>
                  {!viewMode && (
                    <Group pos="absolute" bottom={2} right={2} gap={2}>
                      {i > 1 && <Tooltip label="Move left"><ActionIcon size="xs" variant="filled" color="dark" onClick={() => moveUp(i)}><IconArrowUp size={10} /></ActionIcon></Tooltip>}
                      {i < featured.length - 1 && <Tooltip label="Move right"><ActionIcon size="xs" variant="filled" color="dark" onClick={() => moveDown(i)}><IconArrowDown size={10} /></ActionIcon></Tooltip>}
                      <Tooltip label="Replace"><ActionIcon size="xs" variant="filled" color="dark" onClick={() => { setReplaceSlot(i); setShowPicker(true); }}><IconArrowsExchange size={10} /></ActionIcon></Tooltip>
                      <Tooltip label="Remove"><ActionIcon size="xs" variant="filled" color="red" onClick={() => removeFromFeatured(i)}><IconX size={10} /></ActionIcon></Tooltip>
                    </Group>
                  )}
                </>
              ) : (
                <Paper h="100%" bg="gray.1" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: viewMode ? 'default' : 'pointer' }}
                  onClick={() => !viewMode && (setReplaceSlot(null), setShowPicker(true))}>
                  <Stack align="center" gap={2}><IconPlus size={16} color="gray" /><Text size="xs" c="dimmed">{i + 1}</Text></Stack>
                </Paper>
              )}
            </div>
          ))}
        </div>

        {/* List view for reordering */}
        {featured.length > 0 && !viewMode && (
          <Stack gap="xs">
            <Text size="xs" fw={600} c="dimmed">Current order:</Text>
            {featured.map((photo, i) => (
              <Group key={photo.id} gap="sm" p="xs" style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: '6px' }}>
                <Text size="xs" fw={700} w={20} ta="center">{i + 1}</Text>
                <Image src={getPhotoUrl(photo.photo_url)} w={40} h={30} radius="sm" fit="cover" />
                <Text size="xs" flex={1} lineClamp={1}>{photo.category || 'general'}</Text>
                {i === 0 && <Badge size="xs" color="yellow" variant="light">Main</Badge>}
                <Group gap={2}>
                  <ActionIcon size="xs" variant="subtle" disabled={i === 0} onClick={() => moveUp(i)}><IconArrowUp size={12} /></ActionIcon>
                  <ActionIcon size="xs" variant="subtle" disabled={i === featured.length - 1} onClick={() => moveDown(i)}><IconArrowDown size={12} /></ActionIcon>
                  <ActionIcon size="xs" variant="subtle" color="red" onClick={() => removeFromFeatured(i)}><IconX size={12} /></ActionIcon>
                </Group>
              </Group>
            ))}
          </Stack>
        )}
      </Card>

      {/* Photo Picker Modal */}
      <Modal opened={showPicker} onClose={() => { setShowPicker(false); setReplaceSlot(null); }}
        title={<Text fw={600}>{replaceSlot !== null ? `Replace position ${replaceSlot + 1}` : 'Add photo to gallery'}</Text>}
        size="lg">
        {availablePhotos.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">No available photos. Upload more photos first.</Text>
        ) : (
          <SimpleGrid cols={{ base: 3, sm: 4 }} spacing="xs">
            {availablePhotos.map(photo => (
              <Paper key={photo.id} withBorder p={2} radius="sm"
                style={{ cursor: 'pointer', transition: 'transform 0.1s' }}
                onClick={() => replaceSlot !== null ? replaceInFeatured(photo) : addToFeatured(photo)}>
                <Image src={getPhotoUrl(photo.photo_url)} h={80} radius="sm" fit="cover" />
                <Text size="xs" c="dimmed" ta="center" mt={2}>{photo.category || 'general'}</Text>
              </Paper>
            ))}
          </SimpleGrid>
        )}
      </Modal>
    </>
  );
};

export default GalleryPreview;
